import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import ItemDetailModal from "../../components/ItemDetailModal";
import PackageBuyPromptModal from "../../components/PackageBuyPromptModal";
import ReviewsDisplay from "../../components/ReviewsDisplay"; // Phase G
import LooksGallery from "../../components/LooksGallery";
import ServiceCard from "../../components/ServiceCard";
import PackageOfferCard from "../../components/PackageOfferCard";
import ShopHero from "../../components/ShopHero";
import { formatProfessionalType } from "../../utils/formatters";
import "./ShopLanding.css";

/**
 * Service preview column count. MUST match `.shop-service-grid` in ShopLanding.css — the slice below
 * multiplies this by the number of rows shown, and any disagreement leaves a partly-filled last row.
 */
const getServiceColumns = () => {
  if (typeof window === "undefined") return 4;
  const w = window.innerWidth;
  // These must match the container the section sits in (max 1200px, 48px padding each side) at a
  // 210px minimum card: 5 columns only once there is room for five.
  if (w <= 560) return 2; // phones: two per row
  if (w <= 900) return 3; // tablets
  if (w <= 1200) return 4;
  return 5;
};

/** Rows in the services preview. Two keeps it a summary rather than a second services page. */
const SERVICE_PREVIEW_ROWS = 2;

const ShopLanding = () => {
  const SHOW_PUBLIC_GALLERY = false;
  // ON as of 2026-10-04. The section was already written and already sits directly after Featured
  // Products — it was simply switched off, so a customer could not see the packages this shop sells
  // even though packages are a first-class thing to buy. Products and packages now appear in the
  // same order the home page shows them.
  //
  // Note this is the LIVE customer shop page (`/business/:businessSlug/:locationSlug`).
  // `pages/customer/ShopDetails.js` looks similar and is NOT routed by App.js.
  const SHOW_PUBLIC_PACKAGES = true;
  const { businessSlug, locationSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [packages, setPackages] = useState([]);
  // The package whose "Buy package" prompt is open. A package card here opens the same prompt as a
  // card on the home page — one flow and one set of words wherever a package is found.
  const [buyPromptPackage, setBuyPromptPackage] = useState(null);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [looks, setLooks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [publicNotice, setPublicNotice] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  /**
   * How many service cards fit in a row on this screen — used to show a WHOLE number of rows in the
   * "Services Offered" preview. A fluid grid plus a fixed slice count left an orphan card alone on the
   * last row (5 + 1), which is what the shop owner spotted. The column counts here must match
   * `.shop-service-grid` in ShopLanding.css.
   */
  /**
   * Actual column count of the services preview grid. Measured from the rendered grid rather than guessed
   * from the window, so the slice below can never disagree with what CSS decided — that mismatch is what
   * produced a single orphan card on the last row.
   */
  const [previewColumns, setPreviewColumns] = useState(getServiceColumns());
  const [galleryModal, setGalleryModal] = useState({
    isOpen: false,
    currentIndex: 0,
  });
  const [itemModal, setItemModal] = useState({
    isOpen: false,
    itemType: "product",
    itemId: null,
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setPreviewColumns(getServiceColumns());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!galleryModal.isOpen) return;

      if (e.key === "Escape") {
        setGalleryModal({ isOpen: false, currentIndex: 0 });
      } else if (e.key === "ArrowLeft" && galleryModal.currentIndex > 0) {
        setGalleryModal((prev) => ({
          ...prev,
          currentIndex: prev.currentIndex - 1,
        }));
      } else if (
        e.key === "ArrowRight" &&
        shop?.images &&
        galleryModal.currentIndex < shop.images.length - 1
      ) {
        setGalleryModal((prev) => ({
          ...prev,
          currentIndex: prev.currentIndex + 1,
        }));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [galleryModal, shop?.images]);

  useEffect(() => {
    fetchShopData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessSlug, locationSlug]);

  const fetchShopData = async () => {
    try {
      setLoading(true);
      setError("");
      setPublicNotice(null);

      const shopUrl = `${process.env.REACT_APP_API_BASE_URL}/api/business/${businessSlug}/${locationSlug}`;
      const shopRes = await fetch(shopUrl);

      // Check if response was successful
      if (!shopRes.ok) {
        let errorPayload = null;
        try {
          errorPayload = await shopRes.json();
        } catch (_err) {}

        if (
          errorPayload?.registrationInProgress ||
          errorPayload?.error === "registration_in_progress"
        ) {
          setPublicNotice({
            title: errorPayload?.title || "Shop setup in progress",
            message:
              errorPayload?.message ||
              "Your favorite shop is still registering. Please check back in a few hours.",
            ownerMessage: errorPayload?.ownerMessage || "",
            loginPath: errorPayload?.loginPath || "/login",
          });
          return;
        }

        throw new Error(errorPayload?.message || "Shop location not found");
      }

      // Parse successful response
      const shopData = await shopRes.json();
      setShop(shopData);

      // Fetch packages for this business (public endpoint)
      try {
        const packagesUrl = `${process.env.REACT_APP_API_BASE_URL}/api/packages/business/${shopData.businessId}/public`;
        const packagesRes = await fetch(packagesUrl);
        if (packagesRes.ok) {
          const packagesData = await packagesRes.json();
          const sortedPackages = (packagesData.packages || [])
            .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
            .slice(0, 6);
          setPackages(sortedPackages);
        } else {
        }
      } catch (err) {
      }

      // Fetch featured products for this shop (V2 API)
      try {
        const productsRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/v2/products/shop/${shopData._id}?featured=true&limit=6`,
        );
        if (productsRes.ok) {
          const productsData = await productsRes.json();
          setProducts(productsData.products || []);
        }
      } catch (err) {
      }

      // Fetch deduplicated services for this shop location (public endpoint)
      try {
        const servicesRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/services/shop/${shopData._id}/deduplicated`,
        );
        if (servicesRes.ok) {
          const servicesData = await servicesRes.json();
          setServices(
            // Enough to fill two full rows of the preview at any width, so the cards keep their
            // service-card size instead of stretching to fill a row they cannot complete.
            (servicesData.services || servicesData || []).slice(0, 24),
          );
        }
      } catch (err) {
      }

      // Fetch staff for this shop location
      try {
        const staffRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/staff/by-shop/${shopData._id}`,
        );
        if (staffRes.ok) {
          const staffData = await staffRes.json();
          setStaff(staffData.staff || staffData || []); // Show ALL staff
        }
      } catch (err) {
      }

      // Fetch published looks for this shop
      try {
        const looksRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/looks/public?shopId=${shopData._id}&limit=60`,
        );
        if (looksRes.ok) {
          const looksData = await looksRes.json();
          setLooks(looksData.looks || []);
        }
      } catch (err) {
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBookService = () => {
    navigate(`/business/${businessSlug}/${locationSlug}/services`);
  };

  // Handle "Book This Look" click from LooksGallery.
  // On R1 shop landing, always route to the shop booking page.
  const handleLookBookService = (look) => {
    const params = new URLSearchParams();
    // The booking page expects the ShopLocation's **id** — every other entry point passes one, and
    // `/api/shops/:id` cannot resolve a slug. This passed `locationSlug`, so a booking started from
    // a Look loaded nothing and sat on "Loading booking information...".
    if (shop?._id) params.set("shopId", String(shop._id));
    if (look?.staffId) params.set("staffId", String(look.staffId));
    if (look?.serviceId) params.set("serviceId", String(look.serviceId));
    if (look?._id) params.set("lookId", String(look._id));

    navigate(`/booking?${params.toString()}`);
  };

  if (loading) {
    return (
      <>
        <CustomerHeader />
        <div
          style={{
            padding: "60px 20px",
            textAlign: "center",
            minHeight: "60vh",
          }}
        >
          <div style={{ fontSize: "18px", color: "#6b7280" }}>Loading shop...</div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  const hasSession = Boolean(localStorage.getItem("ogestyle_token"));

  if (publicNotice) {
    return (
      <>
        <CustomerHeader />
        <div
          style={{
            padding: "60px 20px",
            textAlign: "center",
            minHeight: "60vh",
            maxWidth: "760px",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              background: "#f8fbff",
              border: "1px solid #bfdbfe",
              borderRadius: "12px",
              padding: "24px",
            }}
          >
            <div
              style={{
                fontSize: "22px",
                fontWeight: "700",
                color: "#1e3a8a",
                marginBottom: "10px",
              }}
            >
              {publicNotice.title}
            </div>
            <div
              style={{
                fontSize: "16px",
                color: "#334155",
                marginBottom: "10px",
              }}
            >
              {publicNotice.message}
            </div>
            {publicNotice.ownerMessage && (
              <div
                style={{
                  fontSize: "14px",
                  color: "#0f766e",
                  marginBottom: "18px",
                }}
              >
                {publicNotice.ownerMessage}
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              {!hasSession && (
                <button
                  onClick={() => navigate(publicNotice.loginPath || "/login")}
                  style={{
                    padding: "10px 18px",
                    background: "#1d4ed8",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "600",
                  }}
                >
                  Log In to Finish Registration
                </button>
              )}
              <button
                onClick={() => navigate("/")}
                style={{
                  padding: "10px 18px",
                  background: "#e2e8f0",
                  color: "#0f172a",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (error || !shop) {
    return (
      <>
        <CustomerHeader />
        <div
          style={{
            padding: "60px 20px",
            textAlign: "center",
            minHeight: "60vh",
          }}
        >
          <div
            style={{ fontSize: "18px", color: "#ef4444", marginBottom: "20px" }}
          >
            {error || "Shop location not found"}
          </div>
          <button
            onClick={() => navigate("/")}
            style={{
              padding: "12px 24px",
              background: "#2d6da3",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "600",
            }}
          >
            Return to Home
          </button>
        </div>
        <CustomerFooter />
      </>
    );
  }

  const hasProducts = products.length > 0;
  const locationCount = Number(shop?.locationCount || 0);
  const hasMultipleLocations =
    Boolean(shop?.hasMultipleLocations) || locationCount > 1;
  const heroImage = shop.images?.[0]?.url || shop.images?.[0]?.path || null;

  /**
   * How many service cards the preview shows: whole rows only.
   *
   * A fixed count left an orphan card alone on the last row (5 in a 4-column grid, 1 on its own), which
   * is what the shop owner spotted. Rounding DOWN to a multiple of the column count means every row the
   * preview draws is full — and if a shop has fewer services than one row, it shows one row rather than
   * a ragged tail.
   */
  /**
   * Columns the preview may actually use: never more than the number of cards available. A shop with 4
   * services in a 5-column grid left an empty cell on the right, which reads as a broken row.
   */
  const effectiveColumns = Math.max(
    1,
    Math.min(previewColumns, services.length || previewColumns),
  );

  /** Whole rows only: 2 rows where the shop has enough services, otherwise 1. */
  const servicePreviewCount = (() => {
    const fullRows = Math.floor(services.length / effectiveColumns);
    const rows = Math.max(1, Math.min(SERVICE_PREVIEW_ROWS, fullRows));
    return rows * effectiveColumns;
  })();

  return (
    <>
      <CustomerHeader />
      <div className="shop-landing-container">
        {/* Hero Section */}
        <ShopHero
          name={shop.name}
          locationLine={[shop.city, shop.state].filter(Boolean).join(", ")}
          contactLine={`📞 ${shop.phone || ""}${
            shop.email ? ` • ✉️ ${shop.email}` : ""
          }`}
          logoUrl={shop.business?.logo?.url || shop.logoUrl}
          backgroundImage={heroImage}
          schedule={shop.schedule}
          isMobile={isMobile}
          actions={[
            {
              label: "Book Service",
              onClick: handleBookService,
              variant: "primary",
              hideOnMobile: true,
            },
            hasProducts
              ? {
                  label: "Shop Catalogue",
                  mobileLabel: "Shop Products",
                  onClick: () => navigate(`/shop/${locationSlug}/products`),
                  variant: "solid",
                }
              : {
                  label: "Services",
                  onClick: () =>
                    navigate(
                      `/business/${businessSlug}/${locationSlug}/services`,
                    ),
                  variant: "solid",
                  hideOnDesktop: true,
                },
            {
              label: "All Locations",
              mobileLabel: "Locations",
              onClick: () => navigate(`/business/${businessSlug}`),
              variant: "ghost",
              hideOnMobile: !hasMultipleLocations,
              hideOnDesktop: !hasMultipleLocations,
            },
          ]}
        />

        {/* About Section */}
        {shop.business?.description && (
          <div
            style={{
              maxWidth: "1200px",
              margin: "0 auto",
              padding: isMobile ? "8px 16px" : "20px 48px",
            }}
          >
            <p
              style={{
                fontSize: isMobile ? "12px" : "13px",
                color: "#6b7280",
                lineHeight: "1.6",
              }}
            >
              {shop.business.description}
            </p>
          </div>
        )}

        {/* Staff/Professionals Section */}
        {staff.length > 0 && (
          <div
            style={{
              padding: isMobile ? "8px 16px" : "20px 48px",
              background: "transparent",
              maxWidth: "1200px",
              margin: "0 auto",
              paddingBottom: isMobile ? "10px" : "16px",
            }}
          >
            <h2
              className="shop-section-title"
              style={{
                "--section-title-size": isMobile ? "12px" : "13px",
                color: "#111827",
                marginBottom: isMobile ? "6px" : "24px",
              }}
            >
              Meet Our Team
            </h2>
            {/* Fluid by column width, not by breakpoint: 8 fixed columns at 768–1100 px gave every
                professional a ~90 px sliver, which is what "squeezed" was. auto-fill decides the count. */}
            <div className="shop-grid shop-grid--team">
              {staff.map((member) => (
                <div
                  key={member._id}
                  onClick={() => {
                    if (member.slug) {
                      navigate(
                        member.role === "R7"
                          ? `/freelancers/${member.slug}`
                          : member.role === "R4"
                            ? `/freelancer/${member.slug}`
                          : `/business/${businessSlug}/${locationSlug}/professional/${member.slug}`,
                      );
                    }
                  }}
                  style={{
                    textAlign: "center",
                    cursor: member.slug ? "pointer" : "default",
                  }}
                >
                  <div
                    style={{
                      width: isMobile ? "80px" : "96px",
                      height: isMobile ? "80px" : "96px",
                      borderRadius: "50%",
                      background:
                        member.headshotImage || member.profileImage
                          ? `url(${member.headshotImage || member.profileImage}) center/cover`
                          : "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                      margin: "0 auto 12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: isMobile ? "20px" : "32px",
                    }}
                  >
                    {!(member.headshotImage || member.profileImage) && "👨‍💼"}
                  </div>
                  <h3
                    style={{
                      fontSize: isMobile ? "12px" : "14px",
                      fontWeight: "600",
                      color: "#111827",
                      marginBottom: "4px",
                    }}
                  >
                    {member.fullName ||
                      `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
                      "Professional"}
                  </h3>
                  <p
                    style={{
                      fontSize: isMobile ? "11px" : "12px",
                      color: "#6b7280",
                      marginBottom: "8px",
                    }}
                  >
                    {formatProfessionalType(
                      member.professionalType ||
                        member.primaryProfessionalType ||
                        member.professionalTypes?.[0] ||
                        "Stylist",
                    )}
                  </p>
                  {member.rating > 0 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                      }}
                    >
                      <span
                        style={{
                          color: "#f59e0b",
                          fontSize: isMobile ? "11px" : "12px",
                        }}
                      >
                        ★
                      </span>
                      <span
                        style={{
                          fontSize: isMobile ? "11px" : "12px",
                          fontWeight: "600",
                          color: "#111827",
                        }}
                      >
                        {member.rating.toFixed(1)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Services Section */}
        {services.length > 0 && (
          <div
            style={{
              padding: isMobile ? "8px 16px" : "20px 48px",
              background: "white",
              maxWidth: "1200px",
              margin: "0 auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: isMobile ? "6px" : "24px",
              }}
            >
              <h2
                className="shop-section-title"
                style={{
                  "--section-title-size": isMobile ? "12px" : "13px",
                  color: "#111827",
                }}
              >
                Services Offered
              </h2>
              <button
                onClick={() =>
                  navigate(`/business/${businessSlug}/${locationSlug}/services`)
                }
                style={{
                  background:
                    "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                  color: "white",
                  padding: isMobile ? "6px 12px" : "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  fontSize: isMobile ? "11px" : "12px",
                  fontWeight: "600",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(45, 109, 163, 0.3)",
                }}
              >
                View All →
              </button>
            </div>
            <ul
              className="service-tiles shop-service-grid"

              style={{
                // Never more columns than there are cards: a 5-column grid holding 4 services leaves a
                // hole on the right that reads as a broken row.
                gridTemplateColumns: `repeat(${effectiveColumns}, minmax(0, 1fr))`,
              }}
            >
              {services
                .slice(0, servicePreviewCount)
                .map((service) => (
                  <li key={service._id}>
                    <ServiceCard
                      service={service}
                      professionalCount={service.professionalCount || 0}
                      pricePrefix={service.professionalCount > 1 ? "From" : ""}
                      businessType={shop?.business?.type || shop?.type}
                      onSelect={() =>
                        navigate(
                          `/business/${businessSlug}/${locationSlug}/book?serviceName=${encodeURIComponent(service.name)}`,
                        )
                      }
                    />
                  </li>
                ))}
            </ul>
          </div>
        )}

        {/* Looks Section */}
        {looks.length > 0 && (
          <LooksGallery
            looks={looks}
            sectionTitle="Recent Looks"
            isMobile={isMobile}
            onBookService={handleLookBookService}
            viewAllPath={shop?._id ? `/looks/all?shopId=${shop._id}` : null}
            navigate={navigate}
          />
        )}

        {/* Shop Products Section */}
        {hasProducts && (
          <div
            style={{
              padding: isMobile ? "8px 16px" : "20px 48px",
              background: "#ffffff",
              maxWidth: "1200px",
              margin: "0 auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: isMobile ? "6px" : "14px",
              }}
            >
              <h2
                style={{
                  fontWeight: "700",
                  marginBottom: 0,
                  color: "#111827",
                  fontSize: isMobile ? "13px" : "15px",
                }}
              >
                Featured Products
              </h2>
              <button
                onClick={() => navigate(`/shop/${locationSlug}/products`)}
                style={{
                  background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                  color: "white",
                  padding: isMobile ? "6px 12px" : "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  fontSize: isMobile ? "11px" : "12px",
                  fontWeight: "600",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(45, 109, 163, 0.3)",
                }}
              >
                View All →
              </button>
            </div>
            {/* Phones keep the swipeable rail; anything wider gets a fluid grid so a 900 px tablet shows
                four comfortable cards instead of six cramped ones. */}
            <div
              className={
                isMobile ? "shop-product-rail" : "shop-grid shop-grid--products"
              }
            >
              {products.slice(0, 8).map((product) => (
                <div
                  key={product._id}
                  onClick={() =>
                    setItemModal({
                      isOpen: true,
                      itemType: "product",
                      itemId: product._id,
                    })
                  }
                  style={{
                    minWidth: isMobile ? "155px" : "auto",
                    width: isMobile ? "155px" : "auto",
                    height: isMobile ? "200px" : "260px",
                    borderRadius: "12px",
                    overflow: "hidden",
                    background: "white",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    border: "1px solid #e5e7eb",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 16px rgba(0,0,0,0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(0,0,0,0.1)";
                  }}
                >
                  <img
                    src={product.images?.[0] || "/placeholder-product.png"}
                    alt={product.name}
                    style={{
                      width: "100%",
                      height: isMobile ? "110px" : "150px",
                      objectFit: "cover",
                    }}
                  />
                  <div
                    style={{
                      padding: isMobile ? "8px" : "12px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      flex: 1,
                    }}
                  >
                    <h3
                      style={{
                        fontSize: isMobile ? "11px" : "13px",
                        fontWeight: "600",
                        color: "#111827",
                        margin: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {product.name}
                    </h3>
                    <p
                      style={{
                        fontSize: isMobile ? "10px" : "11px",
                        color: "#6b7280",
                        margin: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {Array.isArray(product.category)
                        ? product.category.join(", ")
                        : product.category || "Uncategorized"}
                    </p>
                    <div
                      style={{
                        marginTop: "auto",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: isMobile ? "11px" : "12px",
                          fontWeight: "700",
                          color: "#059669",
                        }}
                      >
                        ${(product.price || 0).toFixed(2)}
                      </span>
                      {typeof product.stock === "number" && (
                        <span
                          style={{
                            fontSize: isMobile ? "10px" : "11px",
                            fontWeight: "600",
                            color: product.stock > 0 ? "#2563eb" : "#ef4444",
                          }}
                        >
                          {product.stock > 0
                            ? `${product.stock} in stock`
                            : "Out of stock"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Shop Packages - Horizontal Scrollable Framework */}
        {SHOW_PUBLIC_PACKAGES && packages.length > 0 && (
          <div
            style={{
              padding: isMobile ? "8px 16px" : "20px 48px",
              background: "#f9fafb",
              overflow: "hidden",
              minHeight: "340px",
              boxSizing: "border-box",
              maxWidth: "1200px",
              margin: "0 auto",
              paddingTop: isMobile ? "24px" : "40px",
            }}
          >
            <div
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Header */}
              <div
                style={{
                  marginBottom: isMobile ? "6px" : "14px",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <div>
                  <h2
                    style={{
                      fontWeight: "700",
                      marginBottom: "2px",
                      color: "#111827",
                      lineHeight: "1.2",
                      fontSize: isMobile ? "12px" : "13px",
                    }}
                  >
                    💸 Package Deals
                  </h2>
                  <p
                    style={{
                      fontSize: isMobile ? "12px" : "13px",
                      color: "#6b7280",
                      lineHeight: "1.4",
                      margin: 0,
                    }}
                  >
                    Exclusive multi-service packages. Get more for less!
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/business/${businessSlug}/${locationSlug}/packages`,
                    )
                  }
                  style={{
                    background:
                      "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                    color: "white",
                    padding: isMobile ? "6px 12px" : "10px 20px",
                    borderRadius: "8px",
                    border: "none",
                    fontSize: isMobile ? "11px" : "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                    boxShadow: "0 2px 8px rgba(45, 109, 163, 0.3)",
                    whiteSpace: "nowrap",
                    flex: "0 0 auto",
                  }}
                >
                  View All →
                </button>
              </div>

              {/* Horizontal scrollable cards */}
              <div
                style={{
                  display: "flex",
                  gap: "18px",
                  overflowX: "auto",
                  overflowY: "visible",
                  paddingBottom: "10px",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  flex: 1,
                }}
              >
                {packages.map((pkg) => (
                  <div
                    key={pkg._id}
                    style={{ minWidth: "220px", width: "220px", height: "220px" }}
                  >
                    <PackageOfferCard
                      pkg={pkg}
                      onSelect={(selected) => setBuyPromptPackage(selected)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Gallery - Hidden for now */}
        <div
          style={{
            display: "none",
            maxWidth: "1200px",
            margin: "0 auto",
            padding: isMobile ? "40px 16px" : "60px 24px",
          }}
        >
          {shop.images && shop.images.length > 1 && (
            <section style={{ marginBottom: isMobile ? "40px" : "60px" }}>
              <h2
                style={{
                  fontSize: isMobile ? "24px" : "32px",
                  fontWeight: "700",
                  marginBottom: "20px",
                  color: "#111827",
                }}
              >
                📸 Gallery
              </h2>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "repeat(2, 1fr)"
                    : "repeat(3, 1fr)",
                  gap: "16px",
                }}
              >
                {shop.images.map((img, idx) => (
                  <div
                    key={idx}
                    onClick={() =>
                      setGalleryModal({ isOpen: true, currentIndex: idx })
                    }
                    style={{
                      height: isMobile ? "150px" : "200px",
                      borderRadius: "12px",
                      overflow: "hidden",
                      background: `url("${img.url || img.path}") center/cover`,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      cursor: "pointer",
                      transition: "transform 0.2s, box-shadow 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.05)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 16px rgba(0,0,0,0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 8px rgba(0,0,0,0.1)";
                    }}
                  />
                ))}
              </div>
            </section>
          )}
        </div>

        {/* CTA Footer */}
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: isMobile ? "8px 16px" : "20px 48px",
          }}
        >
          <div
            style={{
              textAlign: "center",
              padding: isMobile ? "24px 16px" : "60px 40px",
              background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
              borderRadius: "16px",
              color: "white",
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? "12px" : "32px",
                fontWeight: "700",
                marginBottom: isMobile ? "8px" : "16px",
              }}
            >
              Ready to Book?
            </h2>
            <p
              style={{
                fontSize: isMobile ? "12px" : "18px",
                marginBottom: isMobile ? "16px" : "32px",
                opacity: 0.9,
              }}
            >
              Choose from our amazing services and find the perfect time for you
            </p>
            <button
              onClick={handleBookService}
              style={{
                padding: isMobile ? "10px 20px" : "16px 48px",
                background: "white",
                color: "#2d6da3",
                border: "none",
                borderRadius: "12px",
                fontSize: isMobile ? "12px" : "20px",
                fontWeight: "700",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
              }}
            >
              Browse Services →
            </button>
          </div>
        </div>
      </div>

      {/* Gallery Modal */}
      {SHOW_PUBLIC_GALLERY && galleryModal.isOpen && shop.images && (
        <div
          style={{
            display: "flex",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.9)",
            zIndex: 9999,
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setGalleryModal({ isOpen: false, currentIndex: 0 })}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
              width: isMobile ? "100%" : "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() =>
                setGalleryModal({ isOpen: false, currentIndex: 0 })
              }
              style={{
                position: "absolute",
                top: "-40px",
                right: "0",
                background: "white",
                border: "none",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                fontSize: "24px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10000,
              }}
            >
              ×
            </button>

            <img
              src={
                shop.images[galleryModal.currentIndex]?.url ||
                shop.images[galleryModal.currentIndex]?.path
              }
              alt={`Gallery ${galleryModal.currentIndex + 1}`}
              style={{
                width: "100%",
                height: isMobile ? "60vh" : "80vh",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />

            {shop.images.length > 1 && (
              <>
                {galleryModal.currentIndex > 0 && (
                  <button
                    onClick={() =>
                      setGalleryModal((prev) => ({
                        ...prev,
                        currentIndex: prev.currentIndex - 1,
                      }))
                    }
                    style={{
                      position: "absolute",
                      left: isMobile ? "10px" : "-60px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "50px",
                      height: "50px",
                      fontSize: "24px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                    }}
                  >
                    ←
                  </button>
                )}

                {galleryModal.currentIndex < shop.images.length - 1 && (
                  <button
                    onClick={() =>
                      setGalleryModal((prev) => ({
                        ...prev,
                        currentIndex: prev.currentIndex + 1,
                      }))
                    }
                    style={{
                      position: "absolute",
                      right: isMobile ? "10px" : "-60px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "50px",
                      height: "50px",
                      fontSize: "24px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                    }}
                  >
                    →
                  </button>
                )}

                <div
                  style={{
                    position: "absolute",
                    bottom: "-40px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  {galleryModal.currentIndex + 1} / {shop.images.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Reviews Section - Phase G */}
      {shop?._id && (
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: isMobile ? "20px 16px 40px" : "40px 20px 60px",
          }}
        >
          <ReviewsDisplay type="shop" id={shop._id} showResponseUI={false} />
        </div>
      )}

      {/* Buy package prompt — the same flow and wording as the home page, so a package behaves the
          same wherever it is found. */}
      <PackageBuyPromptModal
        pkg={buyPromptPackage}
        onClose={() => setBuyPromptPackage(null)}
      />

      <CustomerFooter />

      {/* Item Detail Modal */}
      <ItemDetailModal
        isOpen={itemModal.isOpen}
        onClose={() =>
          setItemModal({ isOpen: false, itemType: "product", itemId: null })
        }
        itemType={itemModal.itemType}
        itemId={itemModal.itemId}
      />
    </>
  );
};

export default ShopLanding;
